import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import Header from "../components/Header";
import SupportersTicker from "../components/SupportersTicker";
import logging from "../utils/logging";
import { Heart, CheckCircle2, AlertCircle, Loader2, Users as UsersIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MIN_AMOUNT = 100;

function formatMoney(n) {
  return `₦${Number(n || 0).toLocaleString()}`;
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function DonatePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [totals, setTotals] = useState({ total_amount: 0, donation_count: 0 });
  const [recent, setRecent] = useState([]);
  const [amount, setAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [donorPhone, setDonorPhone] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [verifyState, setVerifyState] = useState(null); // "verifying" | "success" | "failed"
  const hasVerified = useRef(false);

  const fetchStats = useCallback(async () => {
    try {
      const [t, r] = await Promise.all([
        axios.get(`${API}/donations/total`),
        axios.get(`${API}/donations/recent?limit=8`),
      ]);
      setTotals(t.data);
      setRecent(r.data);
    } catch (err) {
      logging.warn("stats fetch failed", err);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    // Prefill from logged-in user if not anonymous
    if (user && !isAnonymous) {
      setDonorName((v) => v || user.name || "");
      setDonorEmail((v) => v || user.email || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Post-redirect verification (Flutterwave returns ?tx_ref=&transaction_id=&status=)
  useEffect(() => {
    if (hasVerified.current) return;
    const txRef = params.get("tx_ref");
    const txId = params.get("transaction_id");
    const status = params.get("status");
    if (!txRef) return;
    hasVerified.current = true;

    (async () => {
      if (status === "cancelled") {
        toast.info(t("donate.cancelled"));
        setParams({}, { replace: true });
        return;
      }
      setVerifyState("verifying");
      try {
        const res = await axios.get(`${API}/donations/verify/${txRef}`, {
          params: txId ? { transaction_id: txId } : {},
        });
        if (res.data.status === "successful") {
          setVerifyState("success");
          toast.success(t("donate.thanks_received"));
          await fetchStats();
        } else {
          setVerifyState("failed");
          toast.error(t("donate.not_verified"));
        }
      } catch (err) {
        setVerifyState("failed");
        logging.error("verify failed", err);
      } finally {
        setParams({}, { replace: true });
      }
    })();
  }, [params, setParams, fetchStats, t]);

  const handleDonate = async (e) => {
    e.preventDefault();
    setError("");
    const amt = parseInt(amount, 10);
    if (!amt || amt < MIN_AMOUNT) {
      setError(t("donate.min_donation", { n: MIN_AMOUNT }));
      return;
    }
    if (!isAnonymous && !donorEmail.trim()) {
      setError(t("donate.email_or_anon"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/donations/initiate`,
        {
          amount: amt,
          donor_email: isAnonymous ? null : donorEmail.trim() || null,
          donor_name: isAnonymous ? null : donorName.trim() || null,
          donor_phone: isAnonymous ? null : donorPhone.trim() || null,
          is_anonymous: isAnonymous,
        },
        { withCredentials: true }
      );
      window.location.href = res.data.checkout_url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t("donate.could_not_start"));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6]" data-testid="donate-page">
      {user && <Header />}

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-rose-100 mb-4">
            <Heart className="w-8 h-8 text-rose-600" fill="currentColor" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-gray-900 mb-2">
            {t("donate.title")}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 font-body max-w-md mx-auto">
            {t("donate.subtitle")}
          </p>
        </div>

        {/* Live ticker — rebuilds its own data every 30 s */}
        <SupportersTicker />

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 sm:gap-6 mb-10">
          <div className="bg-white border border-gray-200 rounded-sm p-4 sm:p-6" data-testid="stat-total">
            <p className="text-[10px] sm:text-xs uppercase tracking-widest font-bold text-gray-500 mb-1">{t("donate.raised")}</p>
            <p className="font-heading text-2xl sm:text-4xl font-black text-gray-900">{formatMoney(totals.total_amount)}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-sm p-4 sm:p-6" data-testid="stat-donors">
            <p className="text-[10px] sm:text-xs uppercase tracking-widest font-bold text-gray-500 mb-1">{t("donate.donors")}</p>
            <p className="font-heading text-2xl sm:text-4xl font-black text-gray-900 flex items-center gap-2">
              {totals.donation_count}
              <UsersIcon className="w-5 h-5 text-gray-400" />
            </p>
          </div>
        </div>

        {/* Verify banners */}
        {verifyState === "verifying" && (
          <div className="bg-white border border-gray-200 rounded-sm p-4 mb-6 flex items-center gap-3" data-testid="verify-loading">
            <Loader2 className="w-5 h-5 animate-spin text-gray-700" />
            <p className="text-sm font-body">{t("donate.verifying")}</p>
          </div>
        )}
        {verifyState === "success" && (
          <div className="bg-green-50 border border-green-300 rounded-sm p-4 mb-6 flex items-start gap-3" data-testid="verify-success">
            <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-bold text-gray-900 font-body">{t("donate.thanks_title")}</p>
              <p className="text-xs text-gray-600 font-body">{t("donate.thanks_body")}</p>
            </div>
          </div>
        )}
        {verifyState === "failed" && (
          <div className="bg-orange-50 border border-orange-300 rounded-sm p-4 mb-6 flex items-start gap-3" data-testid="verify-failed">
            <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-bold text-gray-900 font-body">{t("donate.failed_title")}</p>
              <p className="text-xs text-gray-600 font-body">{t("donate.failed_body")}</p>
            </div>
          </div>
        )}

        <div className="grid gap-6 md:grid-cols-2">
          {/* Donation form */}
          <div className="bg-white border border-gray-200 rounded-sm p-6">
            <h2 className="font-heading text-xl font-bold text-gray-900 mb-4">{t("donate.make_donation")}</h2>
            <form onSubmit={handleDonate} noValidate className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-1 block">{t("donate.amount_label")}</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 font-heading font-bold text-gray-500">₦</span>
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={amount}
                    onChange={(e) => { setAmount(e.target.value.replace(/\D/g, "")); setError(""); }}
                    placeholder={t("donate.amount_placeholder", { n: MIN_AMOUNT })}
                    className="rounded-sm h-11 text-base pl-8 font-mono"
                    data-testid="donate-amount-input"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer" data-testid="donate-anonymous-label">
                <Checkbox
                  checked={isAnonymous}
                  onCheckedChange={(v) => setIsAnonymous(Boolean(v))}
                  data-testid="donate-anonymous-checkbox"
                />
                <span className="text-sm font-body text-gray-700">{t("donate.anonymous_label")}</span>
              </label>

              {!isAnonymous && (
                <div className="space-y-3 border-l-2 border-gray-100 pl-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-1 block">{t("donate.email_required")}</Label>
                    <Input
                      type="email"
                      value={donorEmail}
                      onChange={(e) => setDonorEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="rounded-sm text-sm"
                      data-testid="donate-email-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-1 block">{t("donate.name_label")}</Label>
                    <Input
                      value={donorName}
                      onChange={(e) => setDonorName(e.target.value)}
                      placeholder={t("donate.name_placeholder")}
                      className="rounded-sm text-sm"
                      data-testid="donate-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-1 block">{t("donate.phone_label")}</Label>
                    <Input
                      value={donorPhone}
                      onChange={(e) => setDonorPhone(e.target.value)}
                      placeholder={t("donate.phone_placeholder")}
                      className="rounded-sm text-sm"
                      data-testid="donate-phone-input"
                    />
                  </div>
                </div>
              )}

              {error && <p className="text-sm text-red-600 font-body" data-testid="donate-error">{error}</p>}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-rose-600 hover:bg-rose-700 text-white rounded-sm h-11 font-body font-semibold text-sm tracking-wide"
                data-testid="donate-submit"
              >
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {t("donate.opening_checkout")}</> : <><Heart className="w-4 h-4 mr-2" fill="currentColor" /> {amount ? t("donate.donate_btn", { amount: formatMoney(amount) }) : t("donate.donate_btn_bare")}</>}
              </Button>
              <p className="text-[11px] text-gray-400 font-body text-center">
                {t("donate.secure_payment")}
              </p>
            </form>
          </div>

          {/* Recent donors */}
          <div className="bg-white border border-gray-200 rounded-sm p-6">
            <h2 className="font-heading text-xl font-bold text-gray-900 mb-4">{t("donate.recent_donors")}</h2>
            {recent.length === 0 ? (
              <p className="text-sm text-gray-400 font-body text-center py-8">{t("donate.be_first")}</p>
            ) : (
              <ul className="space-y-3">
                {recent.map((r, i) => (
                  <li
                    key={`${r.verified_at || ""}-${r.donor_name || "anon"}-${i}`}
                    className="flex items-center justify-between"
                    data-testid={`recent-${i}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-rose-50 flex items-center justify-center shrink-0">
                        <Heart className="w-4 h-4 text-rose-600" fill="currentColor" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-gray-900 font-body truncate">{r.donor_name}</p>
                        <p className="text-[10px] text-gray-400 font-body">{formatDate(r.verified_at)}</p>
                      </div>
                    </div>
                    <p className="font-heading font-bold text-gray-900 text-sm shrink-0 ml-2">{formatMoney(r.amount)}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
